// The last move, when you cannot see it.
//
// The camera stopped flying to the opponent's move (WP1: `trackPiece` is off
// by default), so the board no longer moves out from under what you were
// reading — and the cost of that is a move played somewhere off screen with
// nothing saying where. This is what says it: a chevron at the edge of the
// free area, on the ray from the middle of the screen toward the square, and
// clicking it takes the view there.
//
// **The marker is the control and Space is the accelerator**, in that order.
// Every camera action has to be reachable by a touch — this game is heading
// for mobile — so the key may never be the only way to do it.
//
// It does **not** register a chrome rect. Its position is computed *from* the
// free area, so contributing to that area would be a loop; and it is a
// transient mark over the board rather than a piece of the chrome the camera
// must stay clear of.

import { useEffect, useRef } from "react";

import { squareName } from "src/shared/board";
import { useGameState } from "src/client/ai/hooks/selectors";
import { useBoardHandle } from "./shell/BoardHandle";
import { inField } from "./keyGuard";
import { HoverCard, Icon, styled, Tile, useBoardPoint, useFreeArea } from "./kit";
import { edgePlacement, lastMove } from "./moveMarker";

/**
 * How far inside the free area the marker sits, in px.
 *
 * Half a rail tile (23) and a little more, so the whole tile is inside the
 * area the chrome leaves free rather than half under the edge of it.
 */
const MARGIN = 30;

/**
 * The marker's own layer. Fixed, at the chrome rung, and it takes a pointer —
 * unlike every other board overlay, because this one is a button.
 */
const Anchor = styled("div")(({ theme }) => ({
  position: "fixed",
  zIndex: theme.kit.z.chrome,
  pointerEvents: "auto"
}));

/**
 * The chevron's rotation carrier.
 *
 * `Icon` takes no `style` — it owns its own — so the rotation goes on a
 * wrapper. It is a per-frame value and therefore `style`, never `sx`: emotion
 * mints a class per distinct value and this one changes as the camera turns.
 */
const Chevron = styled("span")({ display: "flex" });

/** Which way the card opens: toward the board, which is toward the centre. */
const towardBoard = (angle: number): "left" | "right" | "top" | "bottom" => {
  if (angle >= -45 && angle <= 45) return "left";
  if (angle > 45 && angle < 135) return "top";
  if (angle <= -45 && angle > -135) return "bottom";
  return "right";
};

/**
 * The control half of the guard, which is specific to Space: Space is how a
 * focused control is pressed, and this listener calls `preventDefault` — so
 * without it, tabbing to End turn and pressing Space would jump the camera
 * instead of ending the turn. The typing half is `inField`.
 */
const SPACE_TAKERS = new Set(["BUTTON", "A", "SUMMARY", "OPTION"]);

const takesSpace = (event: KeyboardEvent): boolean => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  return SPACE_TAKERS.has(target.tagName) || target.getAttribute("role") === "button";
};

export function LastMoveMarker() {
  const board = useBoardHandle();
  const state = useGameState();
  const free = useFreeArea();

  const move = lastMove(state);
  // Polls `onFrame`, so the marker tracks the camera without re-rendering the
  // shell — the projection is only read back when it has actually moved.
  const point = useBoardPoint(board, move?.to ?? null, "centre");

  const place =
    point &&
    edgePlacement(
      point,
      {
        left: free.x,
        top: free.y,
        right: free.x + free.width,
        bottom: free.y + free.height
      },
      MARGIN
    );

  // Plain, not memoised: `move` is a fresh object every render, so a
  // `useCallback` on it would memoise nothing and only claim to.
  const jump = () => {
    if (!move) return;
    // The jump is the *player's*, so it takes the anchor with it: nothing
    // hands the board back a moment later.
    board.current?.jumpTo([move.from, move.to]);
  };

  // One listener for the life of the component, reading the latest move
  // through a ref — re-registering per move would churn the window's listener
  // set every ply, and closing over `jump` would go stale.
  const armed = useRef<(() => void) | null>(null);
  armed.current = place ? jump : null;
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.code !== "Space" && event.key !== " ") return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (inField(event) || takesSpace(event)) return;
      // Only while the marker is shown: the key is the accelerator for a
      // control, so it does what the control does and nothing when there is
      // no control to press.
      const run = armed.current;
      if (!run) return;
      event.preventDefault();
      run();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);

  if (!move || !place) return null;

  return (
    <Anchor style={{ left: place.x, top: place.y, transform: "translate(-50%, -50%)" }}>
      <HoverCard
        placement={towardBoard(place.angle)}
        title="Last move"
        card={`${squareName(move.from)} → ${squareName(move.to)}`}
        data-testid="card-move-marker"
      >
        <Tile
          variant="rail"
          data-testid="move-marker"
          aria-label="Last move"
          onClick={jump}
        >
          <Chevron style={{ transform: `rotate(${place.angle}deg)` }}>
            <Icon name="chevronRight" />
          </Chevron>
        </Tile>
      </HoverCard>
    </Anchor>
  );
}
